import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import userStore from 'entities/user/module/user-store'

import { MainPageAvia } from 'shared/api/avia/main-page'
import nearFlightInfo from 'shared/images/bg-ticket-info.webp'
import nearFlight from 'shared/images/bg-ticket.webp'
import { formatTimeToValue } from 'shared/lib/getTimeDifference'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ImageLazy } from 'shared/ui/image-lazy'

import { getTimeLeft } from '../libs'
import { nearDirection } from '../model/near-flight-store'

import s from './styles.module.scss'

interface TimerProps {
  time: string
  name: string
}

const Timer = ({ time, name }: TimerProps) => {
  return (
    <div className={s.timerItem}>
      <p className={clsx('black-18')}>{time}</p>
      <p className={clsx('medium-8')}>{name}</p>
    </div>
  )
}

export const NearFlight = observer(() => {
  const { t } = useTranslation()

  const user = userStore.getAccountInfo()

  const getNearFlight = async () => {
    if (nearDirection?.nearTicket) return

    const { data: nearTicket } = await MainPageAvia.getLastTicketBought()

    nearDirection.setNearTicket(nearTicket?.data)
    nearDirection.startTimer(
      getTimeLeft(nearTicket?.data?.departure_datetime_utc),
    )
  }

  useEffect(() => {
    if (user && !nearDirection?.nearTicket) {
      getNearFlight()
    }
  }, [user, nearDirection?.nearTicket])

  const ticket = nearDirection.getNearTicket()
  const timer = nearDirection.getTimer()

  if (!ticket) return null

  const segments = ticket?.flight_details?.flight?.segments

  const ticketInfo = ticket?.restrictions?.at(0)

  const firstSegment = segments?.at(0)
  const lastSegment = segments?.at(-1)
  return (
    <div className={s.wrapper}>
      <div className={s.container}>
        <ImageLazy
          alt="near-flight"
          className={s.image}
          src={Boolean(ticketInfo?.caption) ? nearFlightInfo : nearFlight}
        />

        <div className={s.left}>
          <p className={clsx('extra-bold-14', s.direction)}>
            {`${firstSegment?.dep?.city?.title || ''} - ${lastSegment?.arr?.city?.title || ''}`}
          </p>

          <div className={s.timerWrapper}>
            <p className={clsx('medium-12', s.timerTitle)}>
              {t('flight_after_time')}
            </p>

            <div className={s.timer}>
              {Boolean(timer?.days) && (
                <>
                  <Timer
                    name={t('days')}
                    time={formatTimeToValue(timer?.days)}
                  />

                  <span className="extra-bold-18">:</span>
                </>
              )}

              <Timer
                name={t('hours')}
                time={formatTimeToValue(timer?.hours)}
              />

              <span className="extra-bold-18">:</span>

              <Timer
                name={t('minutes')}
                time={formatTimeToValue(timer?.minutes)}
              />
            </div>
          </div>
        </div>

        <div className={s.right}>
          {Boolean(ticket?.ticket_receipt_url) && (
            <Link
              className={s.reveiptLink}
              to={RoutingPaths.avia.myTickets.detail.path(ticket?.id, 'active')}
            >
              <div className={s.button}>
                <div className={s.iconButton}>
                  <AppIcon
                    width={20}
                    height={15}
                    className={s.icon}
                    id={AllIcons.FLIGHT_TICKETS}
                  />
                </div>
                <p className={clsx('semi-bold-10', s.openText)}>
                  {t('open_mk')}
                </p>
              </div>
            </Link>
          )}
        </div>
      </div>

      {Boolean(ticketInfo?.caption) && (
        <div className={s.info}>
          <p className={clsx('medium-12', s.text)}>{ticketInfo?.caption}</p>
        </div>
      )}
    </div>
  )
})
